import { RefreshCw } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { relative } from "@/lib/relative-time"
import type { MatrixSync } from "@/types/bids"

/** The matrix's provenance strip: rooms & SF come from the master takeoff,
 *  synced explicitly (never silently), staleness always visible. */
export function SyncBanner({
  sync,
  hasWire,
  published,
  busy,
  unmeasured,
  onSync,
}: {
  sync: MatrixSync
  hasWire: boolean
  published: boolean
  busy: boolean
  unmeasured: string[]
  onSync: () => void
}) {
  if (!hasWire) {
    return (
      <div className="callout mt-4" data-testid="sync-banner">
        <p>
          {published
            ? "This plan's master takeoff has no rooms yet — draw it first, then sync."
            : "Draw and publish this plan's master takeoff first — pricing runs against published masters."}
        </p>
      </div>
    )
  }

  return (
    <div className="mt-4 space-y-2" data-testid="sync-banner">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-hairline bg-surface px-4 py-2.5 text-sm">
        <span className="text-ink-muted">
          Rooms &amp; SF from the master takeoff
          {sync && (
            <>
              {" "}
              · synced {relative(sync.synced_at)} · {sync.row_count} rows · v{sync.plan_version}
            </>
          )}
        </span>
        {sync?.stale && (
          <Badge tone="signal" data-testid="sync-stale">
            master republished — re-sync
          </Badge>
        )}
        <Button
          size="sm"
          variant={sync && !sync.stale ? "secondary" : "primary"}
          className="ml-auto"
          disabled={busy}
          onClick={onSync}
          data-testid="sync-takeoff"
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
          {sync ? "Re-sync" : "Sync from takeoff"}
        </Button>
      </div>
      {unmeasured.length > 0 && (
        <div className="callout-signal">
          <p>
            Not measured (uncalibrated sheet): {unmeasured.join(", ")} — synced with blank
            quantities; calibrate the sheet and re-sync.
          </p>
        </div>
      )}
    </div>
  )
}
